import React, { useEffect, useState } from 'react';

export function useHockeyProfile(player) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    let active = true, timer;
    if (!player.squad_profile_url && (!player.simulation || !player.match_id)) { setLoading(false); return () => controller.abort(); }
    const context = {simulation: player.simulation, match_id: player.match_id,
      side: player.side, name: player.name, number: String(player.number || '')};
    const path = player.squad_profile_url || '/sportsim/api/hockey/player-profile';
    const urlFor = request => player.squad_profile_url
      ? path.replace('?', request ? '/request?' : '?')
      : request ? path + '/request' : path + '?' + new URLSearchParams(context);
    const started = Date.now();
    setResult(null); setLoading(true);
    const read = async (request = false) => {
      const response = await fetch(urlFor(request), {
        signal: controller.signal, ...(request ? {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(context)} : {})
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (active) { setResult(data); setLoading(['queued', 'running'].includes(data.fetch_status)); }
      return data;
    };
    const poll = async () => {
      try {
        const data = await read();
        if (active && ['queued', 'running'].includes(data.fetch_status) && Date.now() - started < 30000) timer = setTimeout(poll, 2000);
        else if (active) setLoading(false);
      } catch { if (active) setLoading(false); }
    };
    (async () => {
      try {
        await read();
        if (!active) return;
        const data = await read(true);
        if (active && ['queued', 'running'].includes(data.fetch_status)) timer = setTimeout(poll, 2000);
      } catch { if (active) setLoading(false); }
    })();
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [player.simulation, player.match_id, player.side, player.name, player.number, player.squad_profile_url]);
  return {result, loading};
}

export default function HockeyProfileDetails({result, loading, hidePhotoCredit = false}) {
  const [expanded, setExpanded] = useState(false);
  const profile = result?.profile;
  if (!profile) return <p className="hockey-profile-state" role="status">{loading ? 'Hämtar spelaruppgifter…' : result?.match_status === 'ambiguous' ? 'Spelaren kunde inte identifieras säkert.' : 'Fler spelaruppgifter är inte tillgängliga just nu.'}</p>;
  const seasons = profile.seasons || [];
  return <div className="hockey-profile-details">
    <p className="hockey-profile-facts">
      {profile.birth_date && <span>Född {profile.birth_date}</span>}
      {profile.height_cm && <span>{profile.height_cm} cm</span>}
      {profile.weight_kg && <span>{profile.weight_kg} kg</span>}
      {!!profile.nationalities?.length && <span>{profile.nationalities.join(', ')}</span>}
    </p>
    {profile.youth_team && <p>Moderklubb: {profile.youth_team}</p>}
    {profile.shoots && <p>Skjuter: {profile.shoots === 'Left' ? 'Vänster' : profile.shoots === 'Right' ? 'Höger' : profile.shoots}</p>}
    {!hidePhotoCredit && profile.photo_url && profile.photo_credit && <small>Foto: {profile.photo_credit}</small>}
    {profile.source_current_team && <p>{profile.source_team_season ? `Klubb ${profile.source_team_season}: ` : 'Nuvarande klubb: '}{profile.source_current_team}</p>}
    {!!seasons.length && <section aria-label="Tillgänglig klubbhistorik" className="hockey-profile-history">
      <h3>Tillgänglig historik</h3>
      {(expanded ? seasons : seasons.slice(0, 5)).map((season, index) => <article key={`${season.season}-${season.team_name}-${season.source_row ?? index}`}>
        <strong>{season.season} · {season.team_name}</strong><span>{season.league_name}</span>
        <small>{season.games ?? '–'} matcher{season.goals != null || season.assists != null || season.points != null ? ` · ${season.goals ?? '–'} mål · ${season.assists ?? '–'} assist · ${season.points ?? '–'} poäng` : ''}</small>
        {!!Object.keys(season.goalie_stats || {}).length && <small>{Object.entries(season.goalie_stats).map(([label, value]) => `${label}: ${value}`).join(' · ')}</small>}
      </article>)}
      {seasons.length > 5 && <button type="button" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>{expanded ? 'Visa färre säsonger' : 'Visa all historik'}</button>}
      <small>Statistik: {profile.history_credit || 'SHL / Elite Prospects'}{profile.history_status === 'partial' ? ' · ofullständig' : ''}</small>
    </section>}
    {loading && <p role="status">Uppdaterar sparade uppgifter…</p>}
    <a className="player-profile-link" href={profile.source_url} target="_blank" rel="noopener noreferrer">Spelaruppgifter hos {profile.source || 'SHL'} ↗</a>
    {profile.fetched_at && <small className="hockey-profile-updated">Sparad {new Date(profile.fetched_at * 1000).toLocaleDateString('sv-SE')}</small>}
  </div>;
}
